'use client'

import { useRef, useState, type CSSProperties } from 'react'
import Image from 'next/image'
import type { BaseScreenProps } from '@/features/quiz/types'
import EdFloatingContinueButton from './floating-continue-button'

// Change in SEAR scores out of 100, as shown in the Paper confidence ledger.
const measures = [
  { label: 'Confidence', treatment: 34, placebo: 12 },
  { label: 'Self-esteem', treatment: 37, placebo: 14 },
  { label: 'Sexual relationship', treatment: 33, placebo: 10 },
  { label: 'Relationship overall', treatment: 29, placebo: 9 },
]

export default function EdConfidenceLedgerScreen({
  screenId,
  onChange,
}: Pick<BaseScreenProps, 'screenId' | 'onChange'>) {
  const [continuing, setContinuing] = useState(false)
  const navigating = useRef(false)

  async function continueToBilling() {
    if (navigating.current) return
    navigating.current = true
    setContinuing(true)
    try {
      await onChange(
        screenId,
        { id: screenId, type: 'number', answer: 1 },
        true,
      )
    } finally {
      navigating.current = false
      setContinuing(false)
    }
  }

  return (
    <>
      <section
        className="mx-auto flex w-full max-w-3xl flex-col gap-5 font-sans text-[#0B0B0F] sm:gap-7"
        aria-labelledby="ed-confidence-title"
        data-testid="ed-confidence-ledger"
      >
        <h1
          id="ed-confidence-title"
          className="font-display text-[30px] leading-9 tracking-tight sm:text-[40px] sm:leading-[48px]"
        >
          <span className="block font-extrabold">
            More confident, with NextClinic.
          </span>
          <span className="block font-extralight">
            In bed, and everywhere else.
          </span>
        </h1>

        <div className="space-y-1.5 text-sm leading-5 text-zinc-700 sm:text-[17px] sm:leading-[26px]">
          <p>
            Clinical trials found men on treatment feel{' '}
            <strong className="inline-block rounded bg-[#F2FF5E] px-1.5 text-[#0B0B0F]">
              more confident within 12 weeks.
            </strong>
          </p>
          <p>Not just harder erections.</p>
        </div>

        <div className="-mx-6 flex flex-col gap-4 border-y border-[#ECECEF] px-6 py-5 sm:mx-0 sm:rounded-xl sm:border sm:p-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <h2 className="text-[15px] font-semibold leading-5">
              What changed after 12 weeks
            </h2>
            <ul className="flex items-center gap-3.5 text-[13px] font-medium leading-4 sm:gap-5">
              <li className="flex items-center gap-1.5 sm:gap-2">
                <span
                  aria-hidden="true"
                  className="size-2.5 shrink-0 rounded-[3px] bg-blue-600 sm:size-3"
                />
                On treatment
              </li>
              <li className="flex items-center gap-1.5 text-zinc-700 sm:gap-2">
                <span
                  aria-hidden="true"
                  className="size-2.5 shrink-0 rounded-[3px] bg-zinc-200 sm:size-3"
                />
                Placebo
              </li>
            </ul>
          </div>

          <dl
            className="flex flex-col gap-3"
            aria-label="Change in score out of 100 after 12 weeks"
          >
            {measures.map(({ label, treatment, placebo }) => (
              <div
                key={label}
                className="flex flex-col gap-[5px] sm:flex-row sm:items-center sm:gap-4"
              >
                <dt className="shrink-0 text-sm font-semibold leading-[18px] sm:w-[180px]">
                  {label}
                </dt>
                <dd className="flex min-w-0 flex-1 flex-col gap-1">
                  {[
                    {
                      value: treatment,
                      name: 'On treatment',
                      color: 'bg-blue-600',
                      text: 'font-semibold',
                    },
                    {
                      value: placebo,
                      name: 'Placebo',
                      color: 'bg-zinc-200',
                      text: 'font-medium text-zinc-500',
                    },
                  ].map(({ value, name, color, text }) => (
                    <div
                      key={name}
                      className="flex items-center gap-2 text-xs leading-4"
                    >
                      <span
                        aria-hidden="true"
                        className={`h-2.5 w-[var(--mobile-width)] shrink-0 rounded-[3px] sm:w-[var(--desktop-width)] ${color}`}
                        style={
                          {
                            '--mobile-width': `${((value * 7) / 327) * 100}%`,
                            '--desktop-width': `${((value * 12) / 522) * 100}%`,
                          } as CSSProperties
                        }
                      />
                      <span className={`shrink-0 ${text}`}>
                        <span className="sr-only">{name}: </span>+{value}
                      </span>
                    </div>
                  ))}
                </dd>
              </div>
            ))}
          </dl>

          <div className="flex flex-col divide-y divide-[#ECECEF] border-t border-[#ECECEF] pt-1 sm:flex-row sm:divide-x sm:divide-y-0 sm:pt-5">
            {[
              { value: '8 in 10', label: 'men saw better erections' },
              { value: '9 in 10', label: 'partners had better sex too' },
            ].map(({ value, label }) => (
              <p
                key={value}
                className="flex min-h-14 flex-1 items-center gap-3.5 py-2 sm:min-h-10 sm:py-0 sm:first:pr-6 sm:last:pl-6"
              >
                <strong className="w-[104px] shrink-0 whitespace-nowrap font-display text-[30px] font-extrabold leading-9 tracking-[-0.03em] text-blue-600 sm:w-auto sm:text-[36px] sm:leading-10">
                  {value}
                </strong>
                <span className="text-[15px] font-medium leading-5">
                  {label}
                </span>
              </p>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-4 sm:gap-5">
          <div className="flex items-center gap-3.5 rounded-xl bg-[#F4F4F6] px-4 py-3.5 sm:gap-4 sm:px-5 sm:py-4">
            <div
              aria-hidden="true"
              className="flex w-[108px] shrink-0 -space-x-3 sm:w-[124px] sm:-space-x-4"
            >
              {[1, 2, 3].map((index) => (
                <Image
                  key={index}
                  src={`/images/quiz/ed-confidence-face-${index}.webp`}
                  alt=""
                  width={52}
                  height={52}
                  className="size-11 shrink-0 rounded-full border-2 border-[#F4F4F6] object-cover sm:size-[52px]"
                />
              ))}
            </div>
            <div className="min-w-0 space-y-0.5">
              <p className="text-sm font-semibold leading-5 sm:text-[17px] sm:leading-6">
                You&apos;re not alone in this.
              </p>
              <p className="text-[13px] leading-[18px] text-zinc-700 sm:text-[15px] sm:leading-[22px]">
                100,000+ Australian men trust NextClinic with it.
              </p>
            </div>
          </div>
          <p className="text-xs leading-4 text-zinc-500">
            Chart: change in score out of 100 after 12 weeks, sildenafil vs
            placebo. Althof et al 2006, J Gen Intern Med, 553 men in two
            placebo-controlled trials. Partners: Fisher et al 2004.
          </p>
        </div>
      </section>

      <EdFloatingContinueButton
        disabled={continuing}
        loading={continuing}
        onClick={continueToBilling}
      >
        I&apos;m ready
      </EdFloatingContinueButton>
    </>
  )
}
